import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import PolicyTab from '../../../components/common/PolicyTab';
import ClassContextPicker from '../../../components/common/ClassContextPicker';

// ─── Tier styling ───────────────────────────────────────────────────────────
const TIERS = {
  platinum: { label: 'Platinum', icon: 'bi-gem',    bg: 'linear-gradient(135deg,#e0f2fe,#a7f3d0)', color: '#0c4a6e', border: '#bae6fd', text: '#0369a1' },
  gold:     { label: 'Gold',     icon: 'bi-trophy', bg: 'linear-gradient(135deg,#fef3c7,#fde68a)', color: '#78350f', border: '#fcd34d', text: '#b45309' },
  silver:   { label: 'Silver',   icon: 'bi-award',  bg: 'linear-gradient(135deg,#f1f5f9,#e2e8f0)', color: '#1e293b', border: '#cbd5e1', text: '#475569' },
  bronze:   { label: 'Bronze',   icon: 'bi-medal',  bg: 'linear-gradient(135deg,#fed7aa,#fdba74)', color: '#7c2d12', border: '#fdba74', text: '#9a3412' },
};
const TIER_ORDER = ['platinum', 'gold', 'silver', 'bronze'];

// ─── Tier badge ─────────────────────────────────────────────────────────────
const TierBadge = ({ tier, large = false }) => {
  const t = TIERS[tier] || TIERS.bronze;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: t.bg, color: t.color,
      padding: large ? '6px 14px' : '3px 10px',
      borderRadius: 20, border: `1px solid ${t.border}`,
      fontWeight: 700, fontSize: large ? 14 : 11, letterSpacing: 0.4, textTransform: 'uppercase',
    }}>
      <i className={`bi ${t.icon}`}/>{t.label}
    </span>
  );
};

const blankForm = {
  studentName: '', admissionNumber: '', classGrade: '', section: '',
  academicYear: '', term: '',
  attendanceScore: 0, examsScore: 0, activitiesScore: 0, behaviorScore: 0,
  weights: { attendance: 30, exams: 40, activities: 15, behavior: 15 },
  remarks: '', isManualTierOverride: false, tier: 'bronze',
};

// ─── Main ───────────────────────────────────────────────────────────────────
export default function RankingSystemPage() {
  const { user } = useAuth();
  const role = user?.roleKey || '';
  const canEdit  = ['admin','manager','team_lead','faculty','teacher'].includes(role) || user?.userType === 'super_admin';
  const canAdmin = ['admin','manager'].includes(role) || user?.userType === 'super_admin';

  const [tab, setTab]               = useState('school'); // 'school' | 'class' | 'standard' | 'policy'
  const [filter, setFilter]         = useState({ academicYear: '', term: '' });
  const [contextFilter, setContextFilter] = useState({});
  const [tierFilter, setTierFilter] = useState('');

  const [records, setRecords]       = useState([]);
  const [totals, setTotals]         = useState({ total: 0, platinum: 0, gold: 0, silver: 0, bronze: 0 });
  const [loading, setLoading]       = useState(false);

  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(blankForm);
  const [saving, setSaving]         = useState(false);

  const [autoCalcRunning, setAutoCalcRunning] = useState(false);
  const [toast, setToast]           = useState(null);

  // ─── Fetch ─────────────────────────────────────────────────────────────
  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const view = tab === 'class' ? 'class' : tab === 'standard' ? 'standard' : 'school';
      const params = { view };
      if (contextFilter.classGrade)  params.classGrade   = contextFilter.classGrade;
      if (contextFilter.section)     params.section      = contextFilter.section;
      if (filter.academicYear)       params.academicYear = filter.academicYear;
      if (filter.term)               params.term         = filter.term;
      if (tierFilter)                params.tier         = tierFilter;
      const r = await api.get('/education/rankings', { params });
      setRecords(r.data?.data?.records || r.data?.records || []);
      setTotals(r.data?.data?.totals || r.data?.totals || { total: 0, platinum: 0, gold: 0, silver: 0, bronze: 0 });
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed to load rankings.' });
    }
    setLoading(false);
  }, [tab, filter, contextFilter, tierFilter]);

  useEffect(() => { if (tab !== 'policy') fetch(); }, [tab, fetch]);

  // ─── Form handlers ─────────────────────────────────────────────────────
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setW = (k, v) => setForm(p => ({ ...p, weights: { ...p.weights, [k]: Number(v) || 0 } }));

  const openCreate = () => {
    setEditing(null);
    setForm({ ...blankForm, academicYear: filter.academicYear, term: filter.term });
    setShowForm(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      studentName: r.studentName, admissionNumber: r.admissionNumber || '',
      classGrade: r.classGrade, section: r.section || '',
      academicYear: r.academicYear || '', term: r.term || '',
      attendanceScore: r.attendanceScore || 0, examsScore: r.examsScore || 0,
      activitiesScore: r.activitiesScore || 0, behaviorScore: r.behaviorScore || 0,
      weights: { attendance: 30, exams: 40, activities: 15, behavior: 15, ...r.weights },
      remarks: r.remarks || '',
      isManualTierOverride: !!r.isManualTierOverride,
      tier: r.tier || 'bronze',
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const handleSave = async () => {
    if (!form.studentName.trim()) return setToast({ type: 'danger', msg: 'Student name is required.' });
    if (!form.classGrade)         return setToast({ type: 'danger', msg: 'Class / Grade is required.' });
    setSaving(true);
    try {
      if (editing) await api.put(`/education/rankings/${editing._id}`, form);
      else         await api.post('/education/rankings', form);
      setToast({ type: 'success', msg: 'Saved.' });
      closeForm(); fetch();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Save failed.' });
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this ranking entry?')) return;
    try {
      await api.delete(`/education/rankings/${id}`);
      setToast({ type: 'success', msg: 'Deleted.' }); fetch();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Delete failed.' });
    }
  };

  const runAutoCalc = async () => {
    if (!window.confirm('Auto-calculate rankings from attendance for all students? Existing manual scores will be preserved.')) return;
    setAutoCalcRunning(true);
    try {
      const r = await api.post('/education/rankings/auto-calc', {
        academicYear: filter.academicYear,
        term: filter.term,
        classGrade: contextFilter.classGrade,
        section: contextFilter.section,
        overwrite: false,
      });
      const d = r.data?.data || r.data;
      setToast({ type: 'success', msg: `Scanned ${d.studentsScanned} students. ${d.rankingsCreated} created, ${d.rankingsUpdated} updated.` });
      fetch();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Auto-calc failed.' });
    }
    setAutoCalcRunning(false);
  };

  // ─── Group records by bucket for class/standard view ──────────────────
  const buckets = {};
  records.forEach(r => {
    const key = r.bucketKey || 'school';
    if (!buckets[key]) buckets[key] = { label: r.bucketLabel || key, records: [] };
    buckets[key].records.push(r);
  });
  const bucketKeys = Object.keys(buckets);

  return (
    <div>
      <PageHeader title="Ranking System" icon="bi-trophy"
        subtitle="Auto + manual student rankings with Platinum / Gold / Silver / Bronze tiers"
        actions={(
          <div className="d-flex gap-2">
            {canAdmin && (
              <button className="btn btn-outline-success btn-sm" onClick={runAutoCalc} disabled={autoCalcRunning}>
                {autoCalcRunning ? <span className="spinner-border spinner-border-sm me-1"/> : <i className="bi bi-cpu me-1"/>}
                Auto-Calculate from Attendance
              </button>
            )}
            {canEdit && (
              <button className="btn btn-primary btn-sm" onClick={openCreate}>
                <i className="bi bi-plus-lg me-1"/>Add Ranking
              </button>
            )}
          </div>
        )}/>
      {toast && <AlertToast type={toast.type} message={toast.msg} show={true} onClose={() => setToast(null)} />}

      {/* Tier summary cards */}
      <div className="row g-2 mb-3">
        {TIER_ORDER.map(t => {
          const meta = TIERS[t];
          const count = totals[t] || 0;
          return (
            <div key={t} className="col-6 col-md-3">
              <div className="card h-100 shadow-sm" style={{ borderRadius: 14, background: meta.bg, border: `1px solid ${meta.border}` }}>
                <div className="card-body py-3 px-3 d-flex align-items-center gap-3">
                  <i className={`bi ${meta.icon}`} style={{ fontSize: 32, color: meta.text }}/>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 24, color: meta.color, lineHeight: 1 }}>{count}</div>
                    <div style={{ fontSize: 12, color: meta.text, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>{meta.label}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item"><button className={`nav-link ${tab === 'school' ? 'active' : ''}`} onClick={() => setTab('school')}>
          <i className="bi bi-globe me-1"/>School-Wide
        </button></li>
        <li className="nav-item"><button className={`nav-link ${tab === 'standard' ? 'active' : ''}`} onClick={() => setTab('standard')}>
          <i className="bi bi-bar-chart-steps me-1"/>By Standard / Grade
        </button></li>
        <li className="nav-item"><button className={`nav-link ${tab === 'class' ? 'active' : ''}`} onClick={() => setTab('class')}>
          <i className="bi bi-people me-1"/>By Class & Section
        </button></li>
        <li className="nav-item"><button className={`nav-link ${tab === 'policy' ? 'active' : ''}`} onClick={() => setTab('policy')}>
          <i className="bi bi-journal-text me-1"/>Policy & Manual
        </button></li>
      </ul>

      {/* Filters */}
      {tab !== 'policy' && (
        <div className="card mb-3">
          <div className="card-body">
            <ClassContextPicker value={contextFilter} onChange={setContextFilter} compact fields={['classGrade','section']} />
            <div className="row g-2 mt-2">
              <div className="col-md-2 col-sm-6">
                <label className="form-label small mb-1">Academic Year</label>
                <input className="form-control form-control-sm" placeholder="2026-27" value={filter.academicYear} onChange={e => setFilter(p => ({ ...p, academicYear: e.target.value }))} />
              </div>
              <div className="col-md-2 col-sm-6">
                <label className="form-label small mb-1">Term</label>
                <input className="form-control form-control-sm" placeholder="Term 1" value={filter.term} onChange={e => setFilter(p => ({ ...p, term: e.target.value }))} />
              </div>
              <div className="col-md-2 col-sm-6">
                <label className="form-label small mb-1">Tier</label>
                <select className="form-select form-select-sm" value={tierFilter} onChange={e => setTierFilter(e.target.value)}>
                  <option value="">All tiers</option>
                  {TIER_ORDER.map(t => <option key={t} value={t}>{TIERS[t].label}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lists */}
      {tab !== 'policy' && (loading ? (
        <div className="text-center py-4"><div className="spinner-border text-primary"/></div>
      ) : records.length === 0 ? (
        <div className="card"><div className="card-body text-center text-muted py-5">
          <i className="bi bi-trophy fs-1 d-block mb-2"/>
          No rankings yet. {canEdit && <span>Use <strong>Add Ranking</strong> to enter manually, or <strong>Auto-Calculate</strong> to seed from attendance.</span>}
        </div></div>
      ) : tab === 'school' ? (
        <RankingTable records={records} canEdit={canEdit} canAdmin={canAdmin} onEdit={openEdit} onDelete={handleDelete} />
      ) : (
        <div className="d-flex flex-column gap-3">
          {bucketKeys.map(k => (
            <div key={k} className="card">
              <div className="card-header py-2">
                <span className="fw-semibold"><i className="bi bi-collection me-2 text-primary"/>{buckets[k].label}</span>
                <span className="badge bg-light text-dark border ms-2">{buckets[k].records.length} students</span>
              </div>
              <RankingTable records={buckets[k].records} canEdit={canEdit} canAdmin={canAdmin} onEdit={openEdit} onDelete={handleDelete} />
            </div>
          ))}
        </div>
      ))}

      {tab === 'policy' && <PolicyTab moduleKey="ranking_system" label="Ranking System — Manual & Policy" />}

      {/* ─── Create / Edit Modal ──────────────────────────────────────── */}
      {showForm && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editing ? 'Edit Ranking' : 'Add Ranking'}</h5>
                <button className="btn-close" onClick={closeForm}/>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Student Name <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.studentName} onChange={e => setF('studentName', e.target.value)} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Admission Number</label>
                    <input className="form-control" value={form.admissionNumber} onChange={e => setF('admissionNumber', e.target.value)} />
                  </div>
                  <div className="col-12">
                    <ClassContextPicker
                      value={{ classGrade: form.classGrade, section: form.section }}
                      onChange={(v) => setForm(p => ({ ...p, classGrade: v.classGrade, section: v.section }))}
                      required={['classGrade']}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Academic Year</label>
                    <input className="form-control form-control-sm" value={form.academicYear} onChange={e => setF('academicYear', e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Term</label>
                    <input className="form-control form-control-sm" value={form.term} onChange={e => setF('term', e.target.value)} />
                  </div>

                  <div className="col-12 mt-2">
                    <h6 className="fw-semibold small text-uppercase text-muted">Sub-Scores (0–100)</h6>
                    <div className="row g-2">
                      {[
                        { k: 'attendanceScore', label: 'Attendance', wKey: 'attendance' },
                        { k: 'examsScore',      label: 'Exams',      wKey: 'exams' },
                        { k: 'activitiesScore', label: 'Activities', wKey: 'activities' },
                        { k: 'behaviorScore',   label: 'Behavior',   wKey: 'behavior' },
                      ].map(({ k, label, wKey }) => (
                        <div key={k} className="col-md-3 col-sm-6">
                          <label className="form-label small mb-1">{label}</label>
                          <input type="number" min="0" max="100" className="form-control form-control-sm"
                            value={form[k]} onChange={e => setF(k, parseFloat(e.target.value) || 0)} />
                          <input type="number" min="0" max="100" className="form-control form-control-sm mt-1"
                            value={form.weights[wKey] || 0}
                            onChange={e => setW(wKey, e.target.value)}
                            title="Weight (relative)" placeholder="weight"/>
                        </div>
                      ))}
                    </div>
                    <small className="text-muted d-block mt-1">Bottom field on each = relative weight. Total weights need not sum to 100; relative weighting is what matters.</small>
                  </div>

                  <div className="col-12">
                    <label className="form-label">Remarks</label>
                    <textarea className="form-control" rows={2} value={form.remarks} onChange={e => setF('remarks', e.target.value)} />
                  </div>

                  <div className="col-md-6">
                    <div className="form-check">
                      <input type="checkbox" className="form-check-input" id="manualTier"
                        checked={form.isManualTierOverride} onChange={e => setF('isManualTierOverride', e.target.checked)} />
                      <label htmlFor="manualTier" className="form-check-label">Manually override tier</label>
                    </div>
                  </div>
                  {form.isManualTierOverride && (
                    <div className="col-md-6">
                      <label className="form-label">Override Tier</label>
                      <select className="form-select" value={form.tier} onChange={e => setF('tier', e.target.value)}>
                        {TIER_ORDER.map(t => <option key={t} value={t}>{TIERS[t].label}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={closeForm}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"/>}
                  {editing ? 'Save Changes' : 'Create Ranking'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Reusable table ─────────────────────────────────────────────────────────
function RankingTable({ records, canEdit, canAdmin, onEdit, onDelete }) {
  return (
    <div className="card-body p-0">
      <div className="table-responsive">
        <table className="table table-hover table-sm align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ width: 70 }}>Rank</th>
              <th>Student</th>
              <th>Admission #</th>
              <th>Class · Section</th>
              <th style={{ width: 110 }}>Tier</th>
              <th style={{ width: 90 }}>Total</th>
              <th>Attendance</th>
              <th>Exams</th>
              <th>Activities</th>
              <th>Behavior</th>
              <th>Remarks</th>
              {canEdit && <th style={{ width: 80 }}></th>}
            </tr>
          </thead>
          <tbody>
            {records.map(r => (
              <tr key={r._id}>
                <td className="fw-bold text-center">
                  {r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : `#${r.rank}`}
                </td>
                <td className="fw-semibold">{r.studentName}</td>
                <td className="small text-muted">{r.admissionNumber || '—'}</td>
                <td>{r.classGrade}{r.section ? ` ${r.section}` : ''}</td>
                <td><TierBadge tier={r.tier}/></td>
                <td className="fw-bold">{(r.totalScore || 0).toFixed(1)}</td>
                <td className="small">{r.attendanceScore || 0}</td>
                <td className="small">{r.examsScore || 0}</td>
                <td className="small">{r.activitiesScore || 0}</td>
                <td className="small">{r.behaviorScore || 0}</td>
                <td className="small text-muted text-truncate" style={{ maxWidth: 200 }}>{r.remarks || '—'}</td>
                {canEdit && (
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-outline-primary btn-sm" onClick={() => onEdit(r)} title="Edit">
                        <i className="bi bi-pencil"/>
                      </button>
                      {canAdmin && (
                        <button className="btn btn-outline-danger btn-sm" onClick={() => onDelete(r._id)} title="Delete">
                          <i className="bi bi-trash"/>
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
